import { decodeBase64, encodeBase64, getErrorMessage } from 'utils'
import { z } from 'zod'

// The following codecs are from the zod website, which asserts that they are tested and safe to use: https://zod.dev/codecs

export const stringToNumber = z.codec(z.string().regex(z.regexes.number), z.number(), {
  decode: (str) => Number.parseFloat(str),
  encode: (num) => num.toString()
})

export const stringToInt = z.codec(z.string().regex(z.regexes.integer), z.int(), {
  decode: (str) => Number.parseInt(str, 10),
  encode: (num) => num.toString()
})

export const isoDatetimeToDate = z.codec(z.iso.datetime(), z.date(), {
  decode: (isoString) => new Date(isoString),
  encode: (date) => date.toISOString()
})

export const epochSecondsToDate = z.codec(z.int().min(0), z.date(), {
  decode: (seconds) => new Date(seconds * 1000),
  encode: (date) => Math.floor(date.getTime() / 1000)
})

export const epochMillisToDate = z.codec(z.int().min(0), z.date(), {
  decode: (millis) => new Date(millis),
  encode: (date) => date.getTime()
})

export const jsonCodec = <T extends z.core.$ZodType>(schema: T) =>
  z.codec(z.string(), schema, {
    decode: (jsonString, ctx) => {
      try {
        return JSON.parse(jsonString)
      } catch (err) {
        ctx.issues.push({
          code: 'invalid_format',
          format: 'json',
          input: jsonString,
          message: getErrorMessage(err)
        })
        return z.NEVER
      }
    },
    encode: (value) => JSON.stringify(value)
  })

export const base64Codec = z.codec(z.string(), z.string(), {
  decode: (base64String) => decodeBase64(base64String),
  encode: (plainString) => encodeBase64(plainString)
})

export const base64UrlCodec = z.codec(z.string(), z.string(), {
  decode: (base64String) => decodeBase64(base64String, true),
  encode: (plainString) => encodeBase64(plainString, true)
})

export const uriComponent = z.codec(z.string(), z.string(), {
  decode: (encodedString) => decodeURIComponent(encodedString),
  encode: (decodedString) => encodeURIComponent(decodedString)
})

export const arrayToSet = <T>(itemSchema: z.ZodType<T>) =>
  z.codec(z.array(itemSchema), z.set(itemSchema), {
    decode: (arr) => new Set(arr),
    encode: (set) => Array.from(set as Set<T>)
  })

/**
 * A list carried as one comma-separated string, such as a query parameter. An
 * empty string is an empty list. Items may not be empty or contain a comma,
 * since neither would survive the round trip, so `encode` rejects them.
 */
export const commaSeparatedStrings = z.codec(z.string(), z.array(z.string().regex(/^[^,]+$/)), {
  decode: (joined) => (joined === '' ? [] : joined.split(',')),
  encode: (items) => items.join(',')
})

export const commaSeparatedNumbers = z.codec(z.string(), z.array(z.number()), {
  decode: (joined) => (joined === '' ? [] : joined.split(',').map(Number)),
  encode: (values) => values.join(',')
})
